import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";
import Logo from "@/components/Logo";
import BankFields from "@/components/BankFields";
import StateCitySelect from "@/components/StateCitySelect";
import PinCodeLookup from "@/components/PinCodeLookup";
import { OTHER_BANK } from "@/constants/banks";
import { OTHER_CITY } from "@/constants/cities";
import { toast } from "sonner";
import { CheckCircle2, AlertCircle, Loader2, Upload, X, FileText } from "lucide-react";

const DOC_TYPES = [
  { k: "pan",    l: "PAN Card" },
  { k: "aadhar", l: "Aadhaar" },
  { k: "cheque", l: "Cancelled Cheque" },
  { k: "selfie", l: "Selfie" },
];

/** Debounced verifier — calls /auth/verify-user-code to fetch the associate name
 *  for a typed Sponsor / Placement Parent code (referral doc RF-10).
 */
function useVerifyCode(code) {
  const [status, setStatus] = useState("idle");
  const [info, setInfo] = useState(null);
  useEffect(() => {
    const c = (code || "").trim().toUpperCase();
    if (!c) { setStatus("idle"); setInfo(null); return; }
    let cancelled = false;
    setStatus("loading");
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get(`/auth/verify-user-code?code=${encodeURIComponent(c)}`);
        if (cancelled) return;
        setInfo(data); setStatus("ok");
      } catch (e) {
        if (cancelled) return;
        setInfo({ error: e?.response?.data?.detail || "Unknown associate" });
        setStatus("error");
      }
    }, 350);
    return () => { cancelled = true; clearTimeout(t); };
  }, [code]);
  return { status, info };
}

/** Debounced placement-preview loader — fetches the mini-tree that shows
 *  where the new IBA will land under the chosen parent + side.
 */
function usePlacementPreview(parentCode, side, sponsorCode) {
  const [state, setState] = useState({ status: "idle", data: null });
  useEffect(() => {
    const effectiveParent = (parentCode || sponsorCode || "").trim().toUpperCase();
    const s = (side || "L").toUpperCase();
    if (!effectiveParent) { setState({ status: "idle", data: null }); return; }
    let cancelled = false;
    // Only carry forward previous data if it's a real preview payload (has
    // `parent`). Never carry over an error payload — otherwise render can
    // crash on `d.parent.user_code`.
    setState((prev) => ({ status: "loading", data: prev.data?.parent ? prev.data : null }));
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get(`/auth/placement-preview?parent=${encodeURIComponent(effectiveParent)}&side=${s}`);
        if (cancelled) return;
        setState({ status: "ok", data });
      } catch (e) {
        if (cancelled) return;
        setState({ status: "error", data: { error: e?.response?.data?.detail || "Could not preview placement" } });
      }
    }, 400);
    return () => { cancelled = true; clearTimeout(t); };
  }, [parentCode, side, sponsorCode]);
  return state;
}

export default function Register() {
  const { user: authedUser, register, restoreSession } = useAuth();
  const nav = useNavigate();
  const [busy, setBusy] = useState(false);
  const [agreement, setAgreement] = useState(false);
  const [f, setF] = useState({
    name: "", email: "", phone: "", password: "",
    sponsor_code: "", placement_parent_code: "", placement: "L", role: "distributor",
    full_address: "", dob: "", father_name: "", qualification: "", state: "", city: "", city_other: "", pin_code: "", district: "",
    pan_number: "",
    account_holder: "", bank_name: "", bank_name_other: "",
    bank_account: "", ifsc: "", branch: "",
    // Nominee — captured on the Register form (Feb 2026)
    nominee_name: "", nominee_relation: "", nominee_age: "",
  });
  // Selected doc files (optional — uploaded after account creation)
  const [docs, setDocs] = useState({ pan: null, aadhar: null, cheque: null, selfie: null });
  const fileRefs = useRef({});

  const sponsor = useVerifyCode(f.sponsor_code);
  const parent  = useVerifyCode(f.placement_parent_code);
  const placementPreview = usePlacementPreview(f.placement_parent_code, f.placement, f.sponsor_code);

  const canSubmit = agreement
    && sponsor.status === "ok"
    && (f.placement_parent_code.trim() === "" || parent.status === "ok")
    && !busy;

  const pickFile = (k, file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) return toast.error(`${k.toUpperCase()} file too large — max 5MB`);
    if (!/^(image\/|application\/pdf)/.test(file.type)) return toast.error(`${k.toUpperCase()} must be an image or PDF`);
    setDocs((d) => ({ ...d, [k]: file }));
  };
  const clearFile = (k) => {
    setDocs((d) => ({ ...d, [k]: null }));
    if (fileRefs.current[k]) fileRefs.current[k].value = "";
  };

  const uploadOptionalDocs = async () => {
    // Uploads whichever documents the user selected. Failures are non-blocking —
    // they can always upload later from the KYC section.
    const picked = Object.entries(docs).filter(([, file]) => file);
    if (!picked.length) return { ok: 0, failed: 0 };
    let ok = 0, failed = 0;
    for (const [doc_type, file] of picked) {
      try {
        const fd = new FormData();
        fd.append("doc_type", doc_type);
        fd.append("file", file);
        await api.post("/kyc/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        ok++;
      } catch {
        failed++;
      }
    }
    return { ok, failed };
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!agreement) return toast.error("Please accept the Direct Seller Agreement to continue");
    // Age Gate — must be 18+
    if (f.dob) {
      const dob = new Date(f.dob);
      const today = new Date();
      let age = today.getFullYear() - dob.getFullYear();
      if (today.getMonth() < dob.getMonth() || (today.getMonth() === dob.getMonth() && today.getDate() < dob.getDate())) age--;
      if (age < 18) return toast.error(`You must be at least 18 years old to register (your age: ${age})`);
    }
    // PAN format check — only when user has provided a PAN
    if (f.pan_number.trim()) {
      const pan = f.pan_number.trim().toUpperCase();
      if (!/^[A-Z]{5}[0-9]{4}[A-Z]$/.test(pan)) {
        return toast.error("Invalid PAN — expected format ABCDE1234F");
      }
    }
    // IFSC format check — only when user has provided IFSC
    if (f.ifsc.trim() && !/^[A-Z]{4}0[A-Z0-9]{6}$/.test(f.ifsc.trim().toUpperCase())) {
      return toast.error("Invalid IFSC — expected format ABCD0XXXXXX");
    }
    if (sponsor.status !== "ok") return toast.error("Please enter a valid Sponsor / Referrer ID");
    if (f.placement_parent_code.trim() && parent.status !== "ok") return toast.error("Placement Parent ID is invalid");

    setBusy(true);
    try {
      // If user picked "Other", ship the manual bank_name; drop UI-only field.
      const bank_name_final = f.bank_name === OTHER_BANK
        ? (f.bank_name_other || "").trim()
        : (f.bank_name || "").trim();
      // If user picked "Other" city, ship the manual value.
      const city_final = f.city === OTHER_CITY
        ? (f.city_other || "").trim()
        : (f.city || "").trim();
      const payload = {
        ...f,
        bank_name: bank_name_final,
        city: city_final,
        pan_number: (f.pan_number || "").trim().toUpperCase(),
        ifsc: (f.ifsc || "").trim().toUpperCase(),
        agreement_accepted: true,
      };
      delete payload.bank_name_other;
      delete payload.city_other;
      const result = await register(payload);
      const newUser = result.user;

      if (result.wasLoggedIn) {
        // Downline registration — briefly swap the axios token so the
        // optional KYC files land against the NEW IBA's account, then
        // restore the caller's session so they remain logged in exactly
        // where they were. (Customer rule 18 Feb 2026 — an IBA registering
        // a downline must NOT get logged out.)
        localStorage.setItem("samara_token", result.token);
        const { ok, failed } = await uploadOptionalDocs();
        if (ok)     toast.success(`${ok} document${ok > 1 ? "s" : ""} uploaded for ${newUser.user_code}`);
        if (failed) toast.info(`${failed} document${failed > 1 ? "s" : ""} couldn't upload — new IBA can retry from KYC`);
        await restoreSession(result.preservedToken);
        toast.success(
          `New IBA created — ${newUser.name} (${newUser.user_code}). You remain logged in.`,
          { duration: 4500 },
        );
        // Return the caller to a sensible page. If they came from the tree
        // or genealogy, `-1` sends them back; otherwise their dashboard.
        const canGoBack = window.history.length > 1;
        if (canGoBack) nav(-1);
        else           nav(authedUser?.role === "admin" || authedUser?.role === "superadmin" ? "/admin" : "/tree");
      } else {
        // Fresh public signup — auto-login the new IBA (existing behaviour).
        const { ok, failed } = await uploadOptionalDocs();
        if (ok)     toast.success(`${ok} document${ok > 1 ? "s" : ""} uploaded`);
        if (failed) toast.info(`${failed} document${failed > 1 ? "s" : ""} couldn't upload — try again from KYC section`);
        toast.success(`Welcome ${newUser.name}! Your IBA ID is ${newUser.user_code}`);
        nav("/dashboard");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Registration failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 md:grid-cols-2 bg-white">
      <div className="flex items-center justify-center p-6 sm:p-10 order-2 md:order-1">
        <div className="w-full max-w-md">
          <div className="md:hidden mb-8 flex justify-center"><Logo /></div>
          <div className="text-[11px] uppercase tracking-[0.4em] text-gold">Free Registration</div>
          <h1 className="font-serif text-4xl text-navy mt-2">Start your journey</h1>
          <p className="text-sm text-navy/60 mt-1">Get your unique SAMARA ID instantly.</p>

          <form onSubmit={submit} className="mt-8 space-y-4" autoComplete="on">
            <Field label="Full name"  testid="reg-name"  required value={f.name}  onChange={(v) => setF({ ...f, name: v })} />
            <Field label="Email"      testid="reg-email" type="email" required value={f.email} onChange={(v) => setF({ ...f, email: v })} />
            <Field label="Mobile"     testid="reg-phone" value={f.phone} onChange={(v) => setF({ ...f, phone: v })} />
            <Field label="Password"   testid="reg-password" type="password" required value={f.password} onChange={(v) => setF({ ...f, password: v })} />

            {/* Personal Details */}
            <Section title="Personal Details">
              <Field label="Father's Name" testid="reg-father" required value={f.father_name}
                     onChange={(v) => setF({ ...f, father_name: v })} />
              <Field label="Date of Birth" testid="reg-dob" type="date" required value={f.dob}
                     onChange={(v) => setF({ ...f, dob: v })} />
              <Field label="Qualification" testid="reg-qualification" value={f.qualification}
                     onChange={(v) => setF({ ...f, qualification: v })} />
              <div>
                <label className="text-[11px] uppercase tracking-widest text-navy/60">Full Address <span className="text-rose">*</span></label>
                <textarea
                  data-testid="reg-address"
                  required
                  rows={3}
                  value={f.full_address}
                  onChange={(e) => setF({ ...f, full_address: e.target.value })}
                  placeholder="House / Street / Landmark, Pin Code"
                  className="mt-1 w-full border border-border px-3 py-3 text-sm rounded-sm focus:border-gold focus:outline-none focus:ring-2 focus:ring-gold/20 transition resize-none"
                />
              </div>
              <PinCodeLookup
                pin={f.pin_code}
                onChange={(v) => setF({ ...f, pin_code: v })}
                onResolved={(r) => setF((prev) => ({
                  ...prev,
                  // A fresh PIN lookup is an explicit user action — always
                  // override state + city so the two never drift out of
                  // sync with the resolved postal district.
                  state:      r.state,
                  city:       r.city,
                  city_other: r.cityOther,
                  district:   r.district,
                }))}
                testidPrefix="reg-pin"
              />
              <StateCitySelect
                state={f.state}
                city={f.city}
                cityOther={f.city_other}
                onChange={(v) => setF({ ...f, ...v })}
                required
                testidPrefix="reg"
              />
            </Section>

            {/* PAN Details */}
            <Section title="PAN Details" note="Optional — you can also add this later from the KYC section.">
              <Field
                label="PAN Number"
                testid="reg-pan"
                value={f.pan_number}
                onChange={(v) => setF({ ...f, pan_number: v.toUpperCase() })}
                placeholder="ABCDE1234F"
                mono
              />
            </Section>

            {/* Nominee Details — captured on the Register form */}
            <Section title="Nominee Details" note="Person who will receive your entitlements in the unfortunate event of your demise.">
              <Field
                label="Nominee Name"
                testid="reg-nominee-name"
                value={f.nominee_name}
                onChange={(v) => setF({ ...f, nominee_name: v })}
                placeholder="Full name of the nominee"
              />
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[11px] uppercase tracking-widest text-navy/60">Relation</label>
                  <select
                    data-testid="reg-nominee-relation"
                    value={f.nominee_relation}
                    onChange={(e) => setF({ ...f, nominee_relation: e.target.value })}
                    className="mt-1 w-full border border-border px-3 py-3 text-sm rounded-sm bg-white focus:border-gold focus:outline-none focus:ring-2 focus:ring-gold/20 transition"
                  >
                    <option value="">Select…</option>
                    <option value="Spouse">Spouse</option>
                    <option value="Father">Father</option>
                    <option value="Mother">Mother</option>
                    <option value="Son">Son</option>
                    <option value="Daughter">Daughter</option>
                    <option value="Brother">Brother</option>
                    <option value="Sister">Sister</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                <Field
                  label="Nominee Age"
                  testid="reg-nominee-age"
                  type="number"
                  value={f.nominee_age}
                  onChange={(v) => setF({ ...f, nominee_age: v.replace(/[^0-9]/g, "").slice(0, 3) })}
                  placeholder="e.g. 32"
                />
              </div>
            </Section>

            {/* Account (Bank) Details — dropdown of Indian banks + live IFSC lookup */}
            <Section title="Account Details" note="Optional — required only before your first payout. Type your IFSC and we'll auto-fill bank + branch.">
              <BankFields
                values={{
                  account_holder:  f.account_holder,
                  bank_name:       f.bank_name,
                  bank_name_other: f.bank_name_other,
                  bank_account:    f.bank_account,
                  ifsc:            f.ifsc,
                  branch:          f.branch,
                }}
                onChange={(v) => setF({ ...f, ...v })}
                compact
                testidPrefix="reg-bank"
              />
            </Section>

            {/* Upload Documents (all optional) */}
            <Section title="Upload Documents" note="All optional — upload now or add later from your KYC section (max 5MB each · PNG/JPG/PDF).">
              <div className="space-y-2.5">
                {DOC_TYPES.map((d) => {
                  const picked = docs[d.k];
                  return (
                    <div key={d.k} className="flex items-center justify-between gap-3 border border-border rounded-sm px-3 py-2.5 bg-white">
                      <div className="flex items-center gap-2.5 min-w-0">
                        <FileText size={14} className="text-navy/50 shrink-0" />
                        <div className="min-w-0">
                          <div className="text-sm text-navy">{d.l}</div>
                          {picked ? (
                            <div data-testid={`reg-doc-${d.k}-name`} className="text-[11px] text-olive truncate">
                              {picked.name} · {(picked.size / 1024).toFixed(0)} KB
                            </div>
                          ) : (
                            <div className="text-[11px] text-navy/40">Not selected</div>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-1.5 shrink-0">
                        <input
                          ref={(el) => (fileRefs.current[d.k] = el)}
                          type="file"
                          hidden
                          accept="image/*,application/pdf"
                          data-testid={`reg-doc-${d.k}-input`}
                          onChange={(e) => pickFile(d.k, e.target.files?.[0])}
                        />
                        {picked && (
                          <button
                            type="button"
                            data-testid={`reg-doc-${d.k}-clear`}
                            onClick={() => clearFile(d.k)}
                            className="p-2 text-navy/50 hover:text-rose"
                            aria-label={`Remove ${d.l}`}
                          >
                            <X size={14} />
                          </button>
                        )}
                        <button
                          type="button"
                          data-testid={`reg-doc-${d.k}-pick`}
                          onClick={() => fileRefs.current[d.k]?.click()}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-navy/25 hover:border-gold text-navy text-xs rounded-sm"
                        >
                          <Upload size={12} /> {picked ? "Replace" : "Choose"}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </Section>

            {/* Sponsorship */}
            <Section title="Sponsor & Placement">
              <IdField
                label="Sponsor / Referrer ID"
                hint="Person who introduced you — receives the 10% direct-referral benefit"
                required
                testid="reg-sponsor"
                value={f.sponsor_code}
                onChange={(v) => setF({ ...f, sponsor_code: v.toUpperCase() })}
                verify={sponsor}
              />
              <IdField
                label="Placement Parent ID"
                hint="Under whose team you sit in the binary tree. Leave blank to use Sponsor ID."
                testid="reg-placement-parent"
                value={f.placement_parent_code}
                onChange={(v) => setF({ ...f, placement_parent_code: v.toUpperCase() })}
                verify={parent}
                optional
              />

              <div>
                <div className="text-[11px] uppercase tracking-widest text-navy/60 mb-2">Placement Side</div>
                <div className="grid grid-cols-2 gap-3">
                  {["L", "R"].map((p) => (
                    <button
                      type="button"
                      key={p}
                      data-testid={`reg-place-${p}`}
                      onClick={() => setF({ ...f, placement: p })}
                      className={`py-3 border text-sm rounded-sm transition-colors ${f.placement === p ? "border-gold bg-cream text-navy" : "border-border text-navy/60 hover:border-gold/50"}`}
                    >
                      {p === "L" ? "Left Leg" : "Right Leg"}
                    </button>
                  ))}
                </div>
              </div>

              {/* Placement preview — mini tree showing where the new IBA will land */}
              <PlacementPreview preview={placementPreview} newName={f.name} side={f.placement} />

              <div>
                <div className="text-[11px] uppercase tracking-widest text-navy/60 mb-2">Account type</div>
                <div className="grid grid-cols-2 gap-3">
                  {[{ k: "distributor", l: "IBA" }, { k: "customer", l: "Customer" }].map((p) => (
                    <button
                      type="button"
                      key={p.k}
                      data-testid={`reg-role-${p.k}`}
                      onClick={() => setF({ ...f, role: p.k })}
                      className={`py-3 border text-sm rounded-sm transition-colors ${f.role === p.k ? "border-gold bg-cream text-navy" : "border-border text-navy/60 hover:border-gold/50"}`}
                    >
                      {p.l}
                    </button>
                  ))}
                </div>
              </div>
            </Section>

            <label data-testid="reg-agreement" className="flex items-start gap-2.5 p-3 border border-navy/15 bg-cream/40 rounded-sm cursor-pointer hover:border-gold/40 transition-colors">
              <input
                type="checkbox"
                checked={agreement}
                onChange={(e) => setAgreement(e.target.checked)}
                data-testid="reg-agreement-checkbox"
                className="mt-0.5 accent-gold"
              />
              <span className="text-xs text-navy/80 leading-relaxed">
                I confirm I am <strong>18 years or older</strong> and I have read &amp; accept the{" "}
                <a href="/direct-seller-agreement.pdf" target="_blank" rel="noopener noreferrer" data-testid="reg-agreement-link" className="text-gold-deep underline hover:no-underline">
                  Direct Seller / IBA Agreement — Terms &amp; Conditions
                </a>.
              </span>
            </label>

            <button
              data-testid="reg-submit"
              disabled={!canSubmit}
              className="w-full bg-royal text-white font-medium py-3 hover:bg-royal-deep rounded-sm disabled:opacity-50 transition-colors tracking-wider text-sm"
            >
              {busy ? "Creating account…" : "Create Samara ID"}
            </button>
          </form>

          <p className="mt-6 text-sm text-navy/60 text-center">
            Already have an account?{" "}
            <Link to="/login" data-testid="reg-login-link" className="text-gold font-medium hover:text-gold-deep">Sign in →</Link>
          </p>
        </div>
      </div>

      <div className="relative hidden md:block order-1 md:order-2">
        <img src="https://images.pexels.com/photos/5632335/pexels-photo-5632335.jpeg" alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-br from-rose/80 via-navy/70 to-navy/90" />
        <div className="relative h-full p-12 flex flex-col justify-between text-white">
          <Logo light />
          <div>
            <div className="text-xs uppercase tracking-[0.4em] text-gold">Health · Education · Beauty</div>
            <h2 className="font-serif text-5xl mt-4 leading-tight">Join 10,000+<br/>everyday earners.</h2>
            <p className="mt-6 text-white/75 max-w-md text-sm leading-relaxed">
              Free signup. Activate any package in 15 days and unlock 1:1 weekly matching + startup income from day one.
              Your <strong>Sponsor ID</strong> earns the 10% direct-referral benefit — your <strong>Placement Parent</strong> is where you sit in the tree.
            </p>
          </div>
          <div className="text-xs text-white/40">SAMARA · samarawiz.com</div>
        </div>
      </div>
    </div>
  );
}

function Section({ title, note, children }) {
  return (
    <div className="pt-3 mt-4 border-t border-line/70 space-y-4">
      <div>
        <div className="text-[10px] uppercase tracking-[0.3em] text-gold-deep">{title}</div>
        {note && <div className="text-[11px] text-navy/50 leading-snug mt-1">{note}</div>}
      </div>
      {children}
    </div>
  );
}

function Field({ label, value, onChange, type = "text", required, testid, placeholder, mono }) {
  return (
    <div>
      <label className="text-[11px] uppercase tracking-widest text-navy/60">
        {label} {required && <span className="text-rose">*</span>}
      </label>
      <input
        data-testid={testid}
        type={type}
        required={required}
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        className={`mt-1 w-full border border-border px-3 py-3 text-sm rounded-sm focus:border-gold focus:outline-none focus:ring-2 focus:ring-gold/20 transition ${mono ? "font-mono tracking-wider" : ""}`}
      />
    </div>
  );
}

function IdField({ label, hint, value, onChange, testid, verify, required, optional }) {
  // Ring color is driven by verify.status — but we apply it to a WRAPPER, not
  // the input itself. Swapping the <input>'s className mid-typing dismisses
  // the on-screen keyboard on some mobile browsers (iOS Safari + Chrome
  // Android), which was why the sponsor field "kicked out" after 2–3 chars.
  const ringCls =
    verify.status === "ok"    ? "border-olive/60 focus-within:border-olive focus-within:ring-2 focus-within:ring-olive/20"
    : verify.status === "error" ? "border-rose/60 focus-within:border-rose focus-within:ring-2 focus-within:ring-rose/20"
    : "border-border focus-within:border-gold focus-within:ring-2 focus-within:ring-gold/20";
  return (
    <div>
      <div className="flex items-center justify-between">
        <label className="text-[11px] uppercase tracking-widest text-navy/60">
          {label} {required && <span className="text-rose">*</span>}
          {optional && <span className="text-navy/40 normal-case tracking-normal"> (optional)</span>}
        </label>
        {verify.status === "loading" && <Loader2 size={12} className="text-gold animate-spin" />}
        {verify.status === "ok" && <CheckCircle2 size={12} className="text-olive" />}
        {verify.status === "error" && <AlertCircle size={12} className="text-rose" />}
      </div>
      <div className={`mt-1 border rounded-sm transition-colors ${ringCls}`}>
        <input
          data-testid={testid}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="SM100001"
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="characters"
          spellCheck={false}
          inputMode="text"
          required={required}
          className="w-full bg-transparent px-3 py-3 text-sm font-mono tracking-wider rounded-sm focus:outline-none"
        />
      </div>
      {verify.status === "ok" && verify.info && (
        <div data-testid={`${testid}-name`} className="mt-1 text-xs text-olive flex items-center gap-1.5">
          <CheckCircle2 size={11} /> {verify.info.name} · {verify.info.user_code}
        </div>
      )}
      {verify.status === "error" && verify.info?.error && (
        <div className="mt-1 text-xs text-rose flex items-center gap-1.5">
          <AlertCircle size={11} /> {verify.info.error}
        </div>
      )}
      {verify.status === "idle" && hint && (
        <div className="mt-1 text-[11px] text-navy/50 leading-snug">{hint}</div>
      )}
    </div>
  );
}

/** Mini-tree preview showing where the new IBA will land. Renders the
 *  placement parent + its two children + the incoming card highlighted on
 *  the chosen leg. Also surfaces a warning banner when the direct slot is
 *  filled and BFS spillover will apply.
 */
function PlacementPreview({ preview, newName, side }) {
  if (preview.status === "idle") {
    return (
      <div data-testid="reg-placement-preview-idle" className="rounded-md border border-dashed border-navy/20 bg-cream/30 px-3 py-3 text-[11px] text-navy/50">
        Enter a Sponsor or Placement Parent ID to preview where the new IBA will land.
      </div>
    );
  }
  if (preview.status === "loading" && !preview.data) {
    return (
      <div data-testid="reg-placement-preview-loading" className="rounded-md border border-navy/10 bg-cream/30 px-3 py-3 text-[11px] text-navy/60 flex items-center gap-2">
        <Loader2 size={12} className="animate-spin" /> Loading placement preview…
      </div>
    );
  }
  if (preview.status === "error") {
    return (
      <div data-testid="reg-placement-preview-error" className="rounded-md border border-rose/30 bg-rose/5 px-3 py-3 text-[11px] text-rose flex items-center gap-2">
        <AlertCircle size={12} /> {preview.data?.error || "Could not preview placement"}
      </div>
    );
  }
  const d = preview.data;
  if (!d || !d.parent) return null;
  const S = (side || "L").toUpperCase();
  const spillover = !!d.spillover;
  const chosenChild = S === "L" ? d.left : d.right;
  const otherChild  = S === "L" ? d.right : d.left;
  return (
    <div data-testid="reg-placement-preview" className="rounded-md border border-navy/10 bg-white px-3 py-3">
      <div className="text-[10px] uppercase tracking-widest text-navy/60 font-bold mb-2 flex items-center gap-1.5">
        Placement Preview
        {spillover ? (
          <span className="ml-auto inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 text-[9px] font-extrabold">
            <AlertCircle size={9} /> Spillover
          </span>
        ) : (
          <span className="ml-auto inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-[9px] font-extrabold">
            <CheckCircle2 size={9} /> Direct slot open
          </span>
        )}
      </div>

      {/* Mini tree */}
      <div className="flex flex-col items-center gap-1">
        <MiniCard code={d.parent.user_code} name={d.parent.name} active={d.parent.active} tone="parent" />
        <span className="w-[2px] h-4 bg-sky-400 rounded-full" />
        <div className="flex justify-center gap-6 relative">
          <span className="absolute top-0 left-1/4 right-1/4 h-[2px] bg-sky-400 rounded-full" />
          <div className="flex flex-col items-center gap-1 pt-2">
            <span className="w-[2px] h-2 bg-sky-400 rounded-full" />
            <MiniCard
              code={S === "L" ? (chosenChild?.user_code || "NEW") : (otherChild?.user_code || null)}
              name={S === "L"
                ? (chosenChild ? chosenChild.name : (newName || "New IBA"))
                : (otherChild?.name || null)}
              active={S === "L" ? (chosenChild ? chosenChild.active : true) : otherChild?.active}
              tone={S === "L" ? (chosenChild ? "occupied" : "new") : (otherChild ? "sibling" : "vacant")}
            />
            <span className="text-[9px] font-extrabold uppercase tracking-widest text-emerald-600">Left</span>
          </div>
          <div className="flex flex-col items-center gap-1 pt-2">
            <span className="w-[2px] h-2 bg-sky-400 rounded-full" />
            <MiniCard
              code={S === "R" ? (chosenChild?.user_code || "NEW") : (otherChild?.user_code || null)}
              name={S === "R"
                ? (chosenChild ? chosenChild.name : (newName || "New IBA"))
                : (otherChild?.name || null)}
              active={S === "R" ? (chosenChild ? chosenChild.active : true) : otherChild?.active}
              tone={S === "R" ? (chosenChild ? "occupied" : "new") : (otherChild ? "sibling" : "vacant")}
            />
            <span className="text-[9px] font-extrabold uppercase tracking-widest text-amber-600">Right</span>
          </div>
        </div>
      </div>

      {spillover && (
        <div className="mt-3 rounded bg-amber-50 border border-amber-200 px-2.5 py-1.5 text-[10.5px] text-amber-800 leading-snug">
          <b>Heads-up:</b> {d.parent.user_code}'s {S === "L" ? "left" : "right"} slot is already filled. The system
          will spill the new IBA down the {S === "L" ? "left" : "right"} leg to <b>{d.landing}</b>. Pick a different
          placement parent if you want an exact placement.
        </div>
      )}
    </div>
  );
}

function MiniCard({ code, name, active, tone }) {
  const isNew      = tone === "new";
  const isVacant   = tone === "vacant";
  const isParent   = tone === "parent";
  const cls = isNew
    ? "border-2 border-dashed border-emerald-500 bg-emerald-50/70 animate-pulse"
    : isVacant
      ? "border border-dashed border-navy/20 bg-cream/40 opacity-70"
      : isParent
        ? "border border-sky-500 bg-sky-500/10"
        : active
          ? "border border-emerald-400 bg-emerald-50"
          : "border border-amber-300 bg-amber-50";
  return (
    <div className={`min-w-[102px] rounded-md px-2 py-1.5 text-center ${cls}`}>
      <div className="text-[9px] font-mono text-navy/60 truncate">{code || "\u00A0"}</div>
      <div className="text-[10.5px] font-bold text-navy truncate">{name || (isVacant ? "Vacant" : "—")}</div>
      {isNew && <div className="text-[9px] font-extrabold text-emerald-700 uppercase tracking-widest">New IBA →</div>}
    </div>
  );
}
